import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { useUsers, useSendPasswordReset } from '../hooks/useAdmin';
import { UsersTable } from './UsersTable';
import { UserInviteForm } from './UserInviteForm';
import { UserLocationsForm } from './UserLocationsForm';
import { UserNotificationPreferencesModal } from './UserNotificationPreferencesModal';
import { ConfirmDialog } from './ConfirmDialog';
import type { UserWithLocations } from '../services/usersService';

export function UsersTab() {
  const { showToast } = useToast();
  const { data: users, isLoading } = useUsers();
  const [isInviteFormOpen, setIsInviteFormOpen] = useState(false);
  const [isLocationsFormOpen, setIsLocationsFormOpen] = useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithLocations | undefined>();
  const [notificationsUser, setNotificationsUser] = useState<UserWithLocations | undefined>();
  const [resetPasswordUser, setResetPasswordUser] = useState<
    UserWithLocations | undefined
  >();

  const sendPasswordResetMutation = useSendPasswordReset();

  const handleInvite = () => {
    setIsInviteFormOpen(true);
  };

  const handleEditLocations = (user: UserWithLocations) => {
    setEditingUser(user);
    setIsLocationsFormOpen(true);
  };

  const handleCloseLocationsForm = () => {
    setIsLocationsFormOpen(false);
    setEditingUser(undefined);
  };

  const handleViewNotifications = (user: UserWithLocations) => {
    setNotificationsUser(user);
    setIsNotificationsModalOpen(true);
  };

  const handleCloseNotificationsModal = () => {
    setIsNotificationsModalOpen(false);
    setNotificationsUser(undefined);
  };

  const handleSendPasswordReset = (user: UserWithLocations) => {
    setResetPasswordUser(user);
  };

  const confirmPasswordReset = async () => {
    if (!resetPasswordUser) return;

    try {
      await sendPasswordResetMutation.mutateAsync(resetPasswordUser.email);
      showToast('Password reset email sent successfully', 'success');
      setResetPasswordUser(undefined);
    } catch (error) {
      console.error('Error sending password reset:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to send password reset email',
        'error'
      );
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Users & Roles</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage user accounts and location assignments
          </p>
        </div>
        <Button onClick={handleInvite}>Invite User</Button>
      </div>

      <UsersTable
        users={users || []}
        onEditLocations={handleEditLocations}
        onSendPasswordReset={handleSendPasswordReset}
        onViewNotifications={handleViewNotifications}
        isLoading={isLoading}
      />

      <UserInviteForm
        isOpen={isInviteFormOpen}
        onClose={() => setIsInviteFormOpen(false)}
      />

      <UserLocationsForm
        isOpen={isLocationsFormOpen}
        onClose={handleCloseLocationsForm}
        user={editingUser}
      />

      <UserNotificationPreferencesModal
        isOpen={isNotificationsModalOpen}
        onClose={handleCloseNotificationsModal}
        user={notificationsUser}
      />

      <ConfirmDialog
        isOpen={!!resetPasswordUser}
        onClose={() => setResetPasswordUser(undefined)}
        onConfirm={confirmPasswordReset}
        title="Send Password Reset"
        message={`Send a password reset email to ${resetPasswordUser?.email}?`}
        confirmLabel="Send Email"
        isLoading={sendPasswordResetMutation.isPending}
      />
    </div>
  );
}
